import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, login2fa, loginPasskeyOptions, loginPasskeyVerify, chooseLoginRole, API_URL } from '../services/api';
import { assertPasskey, passkeysSupported, passkeyErrorMessage } from '../utils/webauthn';
import { BRAND_NAME } from '../branding';
import './Login.css';

// Role display names (user identities, 2026-10-30).
const ROLE_LABELS = { administrator: 'Administrator', finance: 'Finance', sales: 'Sales', auditor: 'Auditor (read-only)' };

function Login({ setUser }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Step 2 (two-factor): the password was accepted, a code is still needed.
  const [needCode, setNeedCode] = useState(false);
  const [code, setCode] = useState('');
  // Passkey (administrators): offered as the second step beside the code;
  // passkeyOnly = the account has a passkey but no authenticator app.
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyOnly, setPasskeyOnly] = useState(false);
  // Step 3 (one person, several roles): the sign-in is proven, a role must be
  // chosen. { login_name, roles: [{user_id, role, nickname}], last_user_id }.
  const [roleChoice, setRoleChoice] = useState(null);
  // Why the person is back here (set by the API client on a server-ended
  // session): ?reason=idle = signed out after 1 hour without activity.
  const signedOutIdle = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('reason') === 'idle';
  const navigate = useNavigate();

  // Every sign-in route (password, code, passkey) ends here: either the normal
  // user payload, or "choose how you work now" for a person with several roles.
  const finishSignIn = (data) => {
    if (data?.roleChoiceRequired) {
      setRoleChoice({ login_name: data.login_name, roles: data.roles || [], last_user_id: data.last_user_id });
      setNeedCode(false);
      setCode('');
      setPassword('');
      return;
    }
    setUser(data);
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(username, password);
      if (userData?.twoFactorRequired) {
        setNeedCode(true);
        setCode('');
        setPassword('');
        setPasskeyAvailable(!!userData.passkeyAvailable && passkeysSupported());
        setPasskeyOnly(!!userData.passkeyOnly);
        return;
      }
      finishSignIn(userData);
    } catch (err) {
      console.error('Login error details:', err);

      let errorMessage = 'Login failed.';

      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        errorMessage = 'Cannot reach the server. Check your connection and try again.';
      } else if (err.code === 'ECONNREFUSED') {
        errorMessage = 'The server is not responding.';
      } else if (err.response) {
        const status = err.response.status;
        const serverError = err.response.data?.error;

        if (status === 401) {
          errorMessage = serverError || 'Incorrect username or password.';
        } else if (status === 400) {
          errorMessage = serverError || 'Please fill in both fields.';
        } else if (status === 500) {
          errorMessage = serverError || 'Server error. Please try again later.';
        } else {
          errorMessage = serverError || `Request failed with status ${status}.`;
        }
      } else if (err.request) {
        errorMessage = 'No response from the server.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = (message = '') => {
    setNeedCode(false);
    setCode('');
    setPassword('');
    setPasskeyAvailable(false);
    setPasskeyOnly(false);
    setRoleChoice(null);
    setError(message);
  };

  const handleChooseRole = async (userId) => {
    setError('');
    setLoading(true);
    try {
      const userData = await chooseLoginRole(userId);
      setUser(userData);
      navigate('/dashboard');
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'ROLE_CHOICE_RESTART') backToPassword(data.error || 'Please sign in again.');
      else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') setError('Cannot reach the server. Check your connection and try again.');
      else setError(data?.error || 'Could not choose that role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Second step with a passkey: options → the device prompt → verify.
  const handlePasskey = async () => {
    setError('');
    setLoading(true);
    try {
      const options = await loginPasskeyOptions();
      const answer = await assertPasskey(options);
      const userData = await loginPasskeyVerify(answer);
      finishSignIn(userData);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'TWO_FACTOR_RESTART') backToPassword(data.error || 'Please sign in again.');
      else setError(passkeyErrorMessage(err, 'get'));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    const clean = code.replace(/\s+/g, '').toUpperCase();
    if (!clean) return;
    setError('');
    setLoading(true);
    try {
      const userData = await login2fa(clean);
      finishSignIn(userData);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'TWO_FACTOR_RESTART') {
        backToPassword(data.error || 'Too many wrong codes. Please sign in again.');
      } else if (data?.code === 'TWO_FACTOR_INVALID') {
        const left = typeof data.attempts_left === 'number' ? ` (${data.attempts_left} ${data.attempts_left === 1 ? 'attempt' : 'attempts'} left)` : '';
        setError(`${data.error || 'That code is not correct.'}${left}`);
        setCode('');
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError('Cannot reach the server. Check your connection and try again.');
      } else {
        setError(data?.error || 'Could not verify the code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (roleChoice) {
    // Last-used role first and styled primary; the rest outline.
    const ordered = [...roleChoice.roles].sort((a, b) => (b.user_id === roleChoice.last_user_id) - (a.user_id === roleChoice.last_user_id));
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-brand">{BRAND_NAME}</div>
          <h1 className="login-title">Choose how you work now</h1>
          <p className="login-subtitle">Signed in as {roleChoice.login_name}</p>

          {error && (
            <div className="login-error" role="alert">{error}</div>
          )}

          <div className="login-roles" role="group" aria-label="Choose a role">
            {ordered.map((r, i) => (
              <button
                key={r.user_id}
                type="button"
                className={`btn ${r.user_id === roleChoice.last_user_id ? 'btn-primary' : 'btn-outline'} btn-block login-role-btn`}
                onClick={() => handleChooseRole(r.user_id)}
                disabled={loading}
                autoFocus={i === 0}
                aria-label={`${r.nickname} — ${ROLE_LABELS[r.role] || r.role}`}
              >
                <span className="login-role-name">{r.nickname}</span>
                <span className="login-role-badge">{ROLE_LABELS[r.role] || r.role}</span>
              </button>
            ))}
          </div>

          <div className="tf-links">
            <button type="button" className="tf-link" onClick={() => backToPassword('')} disabled={loading}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  if (needCode) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-brand">{BRAND_NAME}</div>
          <h1 className="login-title">Sign in</h1>
          <p className="login-subtitle">
            {passkeyOnly ? 'Confirm with your passkey' : passkeyAvailable ? 'Use your passkey, or enter the 6-digit code from your authenticator app' : 'Enter the 6-digit code from your authenticator app'}
          </p>

          {error && (
            <div className="login-error" role="alert">{error}</div>
          )}

          {passkeyAvailable && (
            <div className="login-passkey">
              <button type="button" className="btn btn-primary btn-block" onClick={handlePasskey} disabled={loading}>
                {loading ? 'Waiting for your device…' : 'Sign in with passkey'}
              </button>
              {!passkeyOnly && <div className="login-or">or</div>}
            </div>
          )}

          {!passkeyOnly && (
          <form onSubmit={handleCodeSubmit} className="login-form" autoComplete="off">
            <div className="form-group">
              <label htmlFor="twoFactorCode" className="form-label">Code</label>
              <input
                type="text"
                id="twoFactorCode"
                className="form-control"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                placeholder="123456"
              />
            </div>

            <button type="submit" className={`btn ${passkeyAvailable ? 'btn-outline' : 'btn-primary'} btn-block`} disabled={loading || !code.trim()}>
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
          )}

          <div className="tf-links">
            <small className="tf-hint">Lost your phone? Ask an administrator to reset your 2FA in User Management.</small>
            <button type="button" className="tf-link" onClick={() => backToPassword('')} disabled={loading}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-brand">{BRAND_NAME}</div>
        <h1 className="login-title">Sign in</h1>
        <p className="login-subtitle">Access your account</p>

        {signedOutIdle && !error && (
          <div className="login-notice" role="status">
            You were signed out after 1 hour without activity. Sign in again to continue.
          </div>
        )}
        {error && (
          <div className="login-error" role="alert">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
          <div className="form-group">
            <label htmlFor="username" className="form-label">Username</label>
            <input
              type="text"
              id="username"
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              id="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {process.env.NODE_ENV === 'development' && (
          <div className="login-debug">
            API: {API_URL} · {process.env.NODE_ENV}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
